import { getOptionalReports, getReportsForLocation } from "helpers/fakebackend_helper.js"

const monthName = dateStr => {
    const d = new Date(dateStr + "T00:00:00")
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ]
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

// Split range into ~15-day chunks
const splitIntoChunks = (startDate, endDate) => {
    const chunks = [];
    let s = new Date(startDate + "T00:00:00");
    const e = new Date(endDate + "T00:00:00");

    while (s <= e) {
        const chunkStart = new Date(s);
        const chunkEnd = new Date(s);
        chunkEnd.setDate(chunkEnd.getDate() + 7);

        if (chunkEnd > e) chunkEnd.setTime(e.getTime());

        const startStr = chunkStart.toISOString().split("T")[0];
        const endStr = chunkEnd.toISOString().split("T")[0];
        chunks.push({ start: startStr, end: endStr });

        // move to the next day after current end
        chunkEnd.setDate(chunkEnd.getDate() + 1);
        s = new Date(chunkEnd);
    }

    return chunks;
};

// Deep numeric merge
const mergeNumeric = (base, add) => {
    for (const key in add) {
        const val = add[key];

        if (val && typeof val === "object" && !Array.isArray(val)) {
            if (!base[key]) base[key] = {};
            mergeNumeric(base[key], val);
        }
        // ✅ NEW: Handle Arrays (like payments) by concatenating them
        else if (Array.isArray(val)) {
            base[key] = (base[key] || []).concat(val);
        }
        // ✅ Skip merging numeric IDs and static values like daily_lease_amount
        else if (typeof val === "number" && !["id", "location_id", "daily_lease_amount"].includes(key)) {
            base[key] = (base[key] || 0) + val;
        }
        else if (["start_date", "end_date"].includes(key)) {
            if (key === "start_date") {
                if (!base.start_date || new Date(val) < new Date(base.start_date)) {
                    base.start_date = val;
                }
            } else {
                if (!base.end_date || new Date(val) > new Date(base.end_date)) {
                    base.end_date = val;
                }
            }
        }
        else if (["id", "location_id", "daily_lease_amount"].includes(key)) {
            // lock first id or static value, never sum or overwrite
            if (!base[key]) base[key] = val;
        }
        else if (base[key] === undefined) {
            base[key] = val;
        }
    }
    return base;
};

export async function loadReportsHelper({
    token,
    start,
    end,
    included = ["all"],
    selected,
    setProgressMessages,
    setLocations,
    setActiveTab,
}) {
    let reportsObj = {}
    const isAll = selected.length === 0 || selected.some(sel => sel.value === "All Locations")
    const dateChunks = splitIntoChunks(start, end)

    if (isAll) {
        let combined = {};
        let allLocationMap = {}; // keep id:name mapping

        for (const chunk of dateChunks) {
            setProgressMessages(prev => [
                ...prev,
                `Pulling the data for ${monthName(chunk.start)} - ${monthName(chunk.end)} ...`,
            ]);

            // const part = await getReports(token, {
            //     start_date: chunk.start,
            //     end_date: chunk.end,
            // });
            const part = await getOptionalReports(token, {
                start_date: chunk.start,
                end_date: chunk.end,
                included: included.includes("all") ? ["all"] : included
            });

            Object.entries(part).forEach(([locName, data]) => {
                const locId = data?.id;
                if (!combined[locName]) combined[locName] = {};
                combined[locName] = mergeNumeric(combined[locName], data);
                if (locId) allLocationMap[locName] = locId;
            });
        }

        reportsObj = combined;

        const locs = Object.entries(reportsObj)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([locName, locData]) => ({
                label: locName,
                value: allLocationMap[locName] || locData.id || locName
            }));

        setLocations([{ label: "All Locations", value: "All Locations" }, ...locs]);
        setActiveTab("COMBINED");
    } else {
        for (const sel of selected) {
            let merged = {}
            for (const chunk of dateChunks) {
                setProgressMessages(prev => [
                    ...prev,
                    `Pulling the data for ${monthName(chunk.start)} - ${monthName(chunk.end)} ...`,
                ])
                const res = await getReportsForLocation(token, {
                    start_date: chunk.start,
                    end_date: chunk.end,
                    location_id: sel.value,
                })
                merged = mergeNumeric(merged, res.data || {})
            }
            reportsObj[sel.value] = merged
        }
        setActiveTab(selected[0]?.value || "COMBINED")

        // ✅ Fix: ensure full range reflected
        Object.values(reportsObj).forEach(report => {
            if (report) {
                report.start_date = start
                report.end_date = end
            }
        })
    }

    return reportsObj
}